'use client';

import { createLogger, serializeError } from 'logging';
import { useEffect, useMemo, useRef } from 'react';

import { SearchRequest, SearchResponse } from '@videoblocks/events-ts/lib/storyblocks/search/SearchEvent';
import { SearchIdentifiersInterface as SearchIdentifiers } from '@videoblocks/events-ts/lib/storyblocks/search/records/SearchIdentifiers';

import { buildBaseInteractionMeta } from '../shared/BaseEventMeta.client';
import { searchEventAction, type SearchEventActionInput } from './searchEvent';

const logger = createLogger('SearchEventEmitter');

function buildSigningPayload(data: {
    searchRequest: SearchRequest;
    searchResponse: SearchResponse;
    searchIdentifiers: SearchIdentifiers;
}) {
    const { searchRequest } = data;

    return {
        searchRequest,
    };
}

export function stableStringify(value: unknown): string {
    if (value === null) return 'null';
    if (value === undefined) return '"__undef__"';
    const t = typeof value;
    if (t === 'number' || t === 'boolean') return JSON.stringify(value);
    if (t === 'string') return JSON.stringify(value);
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    if (t === 'bigint') return `"${value.toString()}"`;
    if (t === 'function') return '"__func__"';
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => JSON.stringify(k) + ':' + stableStringify(value[k as keyof typeof value])).join(',')}}`;
}

export function stableHash(obj: unknown): string {
    const str = stableStringify(obj);
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = (h * 33) ^ str.charCodeAt(i);
    return (h >>> 0).toString(16);
}

export interface SearchEventData {
    searchRequest: SearchRequest;
    searchResponse: SearchResponse;
    searchIdentifiers: SearchIdentifiers;
}

export interface SearchEventEmitterProps {
    contentClass: 'audio' | 'images' | 'video';
    searchData?: SearchEventData;
    disabled?: boolean;
}

export function SearchEventEmitter({ contentClass, searchData, disabled = false }: SearchEventEmitterProps): null {
    // Compute a key from only the stable payload
    const key = useMemo(() => {
        if (!searchData) return undefined;
        const signing = buildSigningPayload(searchData);
        return stableHash(signing);
    }, [searchData]);

    const emittedKeysRef = useRef<Set<string>>(new Set());
    const pendingKeyRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (disabled || !searchData || !key) return;

        if (emittedKeysRef.current.has(key)) {
            logger.debug?.({ key }, 'skip: already emitted (memory)');
            return;
        }
        if (pendingKeyRef.current === key) {
            logger.debug?.({ key }, 'skip: in-flight');
            return;
        }

        pendingKeyRef.current = key;
        logger.debug?.({ key }, 'emit: starting');

        void (async () => {
            try {
                const { currentUrl, referrer, visitorCookieId } = buildBaseInteractionMeta();

                const evt: SearchEventActionInput = {
                    contentClass,
                    currentUrl,
                    visitorCookieId,
                    searchRequest: searchData.searchRequest,
                    searchResponse: searchData.searchResponse,
                    referrer,
                    newLoginPageFeatureFlag: false,
                    searchIdentifiers: searchData.searchIdentifiers,
                };

                await searchEventAction(evt);

                // mark emitted for this component lifetime
                emittedKeysRef.current.add(key);
                logger.debug?.({ key }, 'emit: success');
            } catch (err) {
                logger.error({ error: serializeError(err) }, 'emit: failed');
            } finally {
                if (pendingKeyRef.current === key) pendingKeyRef.current = undefined;
            }
        })();
    }, [disabled, key, searchData]);

    return null;
}
